// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  ClientOnboardingStateResponse,
  OnboardingClientData,
  OnboardingFiscalAddress,
  OnboardingKycAnswerResponse,
  OnboardingKycAnswerValueResponse,
  OnboardingPostalAddress,
} from '@shared/data-access/types/engine';
import produce from 'immer';
import {
  type AccountOpeningFormData,
  type AccountUsageFormData,
  EmploymentStatus,
  type LegalDeclarationsFormData,
  type PersonalDataFormData,
  type ProfessionalProfileFormData,
  type WealthSourceFundsFormData,
  getInversisOptionId,
  getInversisOptionSlug,
} from '../types';

type AccountOpeningFormSections = Omit<AccountOpeningFormData, 'signature'>;

type MutableAccountOpeningFormSections = {
  personalData: PersonalDataFormData;
  professionalProfile: ProfessionalProfileFormData;
  wealthSourceFunds: WealthSourceFundsFormData;
  accountUsage: AccountUsageFormData;
  legalDeclarations: LegalDeclarationsFormData;
};

const ACCOUNT_OPENING_DEFAULTS = {
  personalData: {
    first_name: '',
    second_name: '',
    first_surname: '',
    second_surname: '',
    birth_date: '',
    marital_status: 'single',
    gender: 'male',
    birth_city: '',
    nationality: 'spain',
    document_type: 'dni-nif',
    document_number: '',
    document_expiration_date: '',
    document_permanent_validity: false,
    email: '',
    phone_number: '',
    postal_address: {
      street_type: '',
      street_name: '',
      street_number: '',
      additional_address_info: '',
      province: '',
      postal_code: '',
      city: '',
      country: 'spain',
    },
    has_different_fiscal_address: false,
    fiscal_address: undefined,
  } satisfies PersonalDataFormData,
  professionalProfile: {
    employmentStatus: EmploymentStatus.Employed,
    companyName: '',
    sector: '',
    companyActivity: '',
  } satisfies Extract<
    ProfessionalProfileFormData,
    { employmentStatus: EmploymentStatus.Employed }
  >,
  wealthSourceFunds: {
    mainIncomeSource: 'income-salary-pension',
    estimatedAnnualIncome: 'annual-50k-100k',
    approximateWealth: 'between-100k-300k',
    sourceOfFunds: ['funds-salary'],
  } satisfies WealthSourceFundsFormData,
  accountUsage: {
    accountPurpose: ['savings', 'investment'],
    monthlyOperations: 'ops-tier-low',
    monthlyTransfers: 'transfers-tier-low',
    accountCheques: 'cheques-no',
  } satisfies AccountUsageFormData,
  legalDeclarations: {
    taxResidenceCountry: 'spain',
    taxResidenceDocumentNumber: '',
    investmentOwnership: 'self',
    isPoliticallyExposedPerson: false,
    isRelatedToPublicPerson: false,
    isUsTaxResidence: false,
  } satisfies LegalDeclarationsFormData,
} satisfies MutableAccountOpeningFormSections;

const DEFAULT_COUNTRY = 'spain';

const createEmptyAddress = (
  country: string = DEFAULT_COUNTRY,
): PersonalDataFormData['postal_address'] => ({
  street_type: '',
  street_name: '',
  street_number: '',
  additional_address_info: '',
  province: '',
  postal_code: '',
  city: '',
  country,
});

const INVERSIS_GENDER_TO_SLUG = {
  H: 'male',
  M: 'female',
} as const satisfies Record<string, PersonalDataFormData['gender']>;

const INVERSIS_MARITAL_STATUS_TO_SLUG = {
  S: 'single',
  C: 'married',
} as const satisfies Record<string, PersonalDataFormData['marital_status']>;

type InversisOptionNamespace =
  | 'countries'
  | 'vias'
  | 'provinces'
  | 'documentTypes'
  | 'sectors'
  | 'activities'
  | 'employmentStatus';

type KycQuestionCode =
  | 'ACCOUNT_PURPOSE'
  | 'MONTHLY_OPS'
  | 'MONTHLY_TRANSFERS'
  | 'ACCOUNT_CHEQUES'
  | 'FUNDS_ORIGIN'
  | 'ESTIMATED_WEALTH'
  | 'MAIN_INCOME'
  | 'ANNUAL_INCOME'
  | 'PUBLIC_ROLE'
  | 'PRP_EXTRA_INCOME'
  | 'PEP_RELATED';

export const buildAccountOpeningFormData = (
  state: ClientOnboardingStateResponse,
): AccountOpeningFormSections => {
  const kycByQuestion = indexKycAnswers(state.kyc_answers);
  const clientData = state.client_data;

  return produce(
    ACCOUNT_OPENING_DEFAULTS,
    (draft: MutableAccountOpeningFormSections) => {
      if (clientData) {
        hydratePersonalDataFromClientData(draft.personalData, clientData);
        hydrateProfessionalProfileFromClientData(draft, clientData);
      }

      hydrateWealthSourceFundsFromKyc(draft.wealthSourceFunds, kycByQuestion);
      hydrateAccountUsageFromKyc(draft.accountUsage, kycByQuestion);
      hydrateLegalDeclarationsFromOnboarding(
        draft.legalDeclarations,
        kycByQuestion,
        clientData,
      );

      if (!draft.legalDeclarations.isUsTaxResidence) {
        draft.legalDeclarations.taxResidenceDocumentNumberEdited =
          draft.legalDeclarations.taxResidenceDocumentNumber?.trim() !==
          draft.personalData.document_number?.trim();
      }
    },
  );
};

const indexKycAnswers = (
  kycAnswers: OnboardingKycAnswerResponse[] | null | undefined,
): Map<number, OnboardingKycAnswerResponse> => {
  return new Map(
    (kycAnswers ?? []).map(answer => [answer.identificadorPregunta, answer]),
  );
};

const getQuestionId = (questionCode: KycQuestionCode): number => {
  return Number(getInversisOptionId(questionCode, 'kycQuestions'));
};

const getKycAnswerSlug = (answerId: number): string | undefined => {
  const key = getInversisOptionSlug(String(answerId), 'kycAnswers');

  if (!key) {
    return undefined;
  }

  const dotIndex = key.indexOf('.');

  return dotIndex >= 0 ? key.slice(dotIndex + 1) : key;
};

const getKycQuestionAnswers = (
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
  questionCode: KycQuestionCode,
): OnboardingKycAnswerValueResponse[] => {
  return kycByQuestion.get(getQuestionId(questionCode))?.datosRespuesta ?? [];
};

const getKycOpenTextCsv = (
  answers: OnboardingKycAnswerValueResponse[],
  questionId: number,
): string | undefined => {
  return answers.find(answer => answer.identificadorRespuesta === questionId)
    ?.valorRespuesta;
};

const splitCsvParts = (csv: string | undefined): string[] => {
  if (!csv?.trim()) {
    return [];
  }

  return csv.split(',').map(part => part.trim());
};

const getKycPredefinedAnswerSlugs = (
  answers: OnboardingKycAnswerValueResponse[],
  questionId: number,
): string[] => {
  return answers
    .filter(answer => answer.identificadorRespuesta !== questionId)
    .map(answer => getKycAnswerSlug(answer.identificadorRespuesta))
    .filter((slug): slug is string => Boolean(slug));
};

const getKycSingleAnswerSlug = (
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
  questionCode: KycQuestionCode,
  fallback: string,
): string => {
  const questionId = getQuestionId(questionCode);
  const answers = getKycQuestionAnswers(kycByQuestion, questionCode);
  const slugs = getKycPredefinedAnswerSlugs(answers, questionId);

  return slugs[0] ?? fallback;
};

const getKycMultipleAnswerSlugs = (
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
  questionCode: KycQuestionCode,
  fallback: string[],
): string[] => {
  const questionId = getQuestionId(questionCode);
  const answers = getKycQuestionAnswers(kycByQuestion, questionCode);
  const slugs = getKycPredefinedAnswerSlugs(answers, questionId);

  return slugs.length > 0 ? slugs : fallback;
};

const hasKycYesAnswer = (
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
  questionCode: KycQuestionCode,
  yesAnswerSlug: string,
): boolean => {
  const answers = getKycQuestionAnswers(kycByQuestion, questionCode);
  const yesAnswerId = Number(
    getInversisOptionId(`${questionCode}.${yesAnswerSlug}`, 'kycAnswers'),
  );

  return answers.some(answer => answer.identificadorRespuesta === yesAnswerId);
};

const toFormOptionSlug = (
  value: string | null | undefined,
  namespace: InversisOptionNamespace,
): string => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return '';
  }

  const slugFromInversisId = getInversisOptionSlug(trimmed, namespace);

  if (slugFromInversisId) {
    return slugFromInversisId;
  }

  return trimmed;
};

const toFormGenderSlug = (
  value?: string | null,
): PersonalDataFormData['gender'] => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return ACCOUNT_OPENING_DEFAULTS.personalData.gender;
  }

  const inversisSlug =
    INVERSIS_GENDER_TO_SLUG[
      trimmed.toUpperCase() as keyof typeof INVERSIS_GENDER_TO_SLUG
    ];

  if (inversisSlug) {
    return inversisSlug;
  }

  const normalized = trimmed.toLowerCase();

  return normalized === 'female' || normalized === 'f' ? 'female' : 'male';
};

const toFormMaritalStatusSlug = (
  value?: string | null,
): PersonalDataFormData['marital_status'] => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return ACCOUNT_OPENING_DEFAULTS.personalData.marital_status;
  }

  const inversisSlug =
    INVERSIS_MARITAL_STATUS_TO_SLUG[
      trimmed.toUpperCase() as keyof typeof INVERSIS_MARITAL_STATUS_TO_SLUG
    ];

  if (inversisSlug) {
    return inversisSlug;
  }

  return trimmed.toLowerCase() === 'married' ? 'married' : 'single';
};

const isoDateToFormDate = (value?: string | null): string => {
  if (!value?.trim()) {
    return '';
  }

  return value.slice(0, 10);
};

const splitFullName = (
  fullName: string | undefined,
): Pick<PersonalDataFormData, 'first_name' | 'second_name'> => {
  const trimmed = fullName?.trim() ?? '';

  if (!trimmed) {
    return { first_name: '', second_name: '' };
  }

  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return { first_name: trimmed, second_name: '' };
  }

  return {
    first_name: trimmed.slice(0, spaceIndex),
    second_name: trimmed.slice(spaceIndex + 1).trim(),
  };
};

const formatPhoneNumber = (phone?: {
  prefijoTelefonico?: string;
  telefono?: string;
}): string => {
  if (!phone?.telefono?.trim()) {
    return '';
  }

  const prefix = phone.prefijoTelefonico?.trim() ?? '';

  if (!prefix) {
    return phone.telefono.trim();
  }

  const normalizedPrefix = prefix.startsWith('+') ? prefix : `+${prefix}`;

  return `(${normalizedPrefix}) ${phone.telefono.trim()}`;
};

const addressesAreDifferent = (
  postal?: OnboardingPostalAddress,
  fiscal?: OnboardingFiscalAddress,
): boolean => {
  if (!postal || !fiscal) {
    return false;
  }

  return (
    postal.codigoPostal !== fiscal.codigoPostal ||
    postal.codigoProvincia !== fiscal.codigoProvincia ||
    postal.localidad !== fiscal.localidad ||
    postal.nombreVia !== fiscal.nombreVia ||
    postal.numeroVia !== fiscal.numeroVia ||
    postal.codigoTipoVia !== fiscal.codigoTipoVia ||
    (postal.datosAdicionalesDireccion ?? '') !==
      (fiscal.datosAdicionalesDireccion ?? '')
  );
};

const hydratePostalAddressFromClientData = (
  draft: PersonalDataFormData['postal_address'],
  address: OnboardingPostalAddress,
): void => {
  const country =
    toFormOptionSlug(address.codigoPaisDomicilioPostal, 'countries') || 'spain';

  draft.country = country;
  draft.street_type =
    country === 'spain' ? toFormOptionSlug(address.codigoTipoVia, 'vias') : '';
  draft.street_name = address.nombreVia ?? '';
  draft.street_number = address.numeroVia ?? '';
  draft.additional_address_info = address.datosAdicionalesDireccion ?? '';
  draft.province =
    country === 'spain'
      ? toFormOptionSlug(address.codigoProvincia, 'provinces')
      : '';
  draft.postal_code = address.codigoPostal ?? '';
  draft.city = address.localidad ?? '';
};

const hydrateFiscalAddressFromClientData = (
  draft: NonNullable<PersonalDataFormData['fiscal_address']>,
  address: OnboardingFiscalAddress,
  country: string,
): void => {
  const resolvedCountry = country || 'spain';

  draft.country = resolvedCountry;
  draft.street_type =
    resolvedCountry === 'spain'
      ? toFormOptionSlug(address.codigoTipoVia, 'vias')
      : '';
  draft.street_name = address.nombreVia ?? '';
  draft.street_number = address.numeroVia ?? '';
  draft.additional_address_info = address.datosAdicionalesDireccion ?? '';
  draft.province =
    resolvedCountry === 'spain'
      ? toFormOptionSlug(address.codigoProvincia, 'provinces')
      : '';
  draft.postal_code = address.codigoPostal ?? '';
  draft.city = address.localidad ?? '';
};

const hydratePersonalDataFromClientData = (
  personalData: PersonalDataFormData,
  clientData: OnboardingClientData,
): void => {
  const defaults = ACCOUNT_OPENING_DEFAULTS.personalData;
  const postalAddressRow = clientData.datosDomicilioPostal?.[0];
  const fiscalAddressRow = clientData.datosDomicilioFiscal?.[0];
  const phoneRow = clientData.datosTelefono?.[0];
  const documentPermanentValidity = clientData.indicadorCaducidadDocumento === 'S';
  const { first_name, second_name } = splitFullName(clientData.nombre);
  const hasDifferentFiscalAddress = addressesAreDifferent(
    postalAddressRow,
    fiscalAddressRow,
  );

  personalData.first_name = first_name || defaults.first_name;
  personalData.second_name = second_name || defaults.second_name;
  personalData.first_surname = clientData.primerApellido ?? defaults.first_surname;
  personalData.second_surname =
    clientData.segundoApellido ?? defaults.second_surname;
  personalData.birth_date =
    isoDateToFormDate(clientData.fechaNacimiento) || defaults.birth_date;
  personalData.marital_status = toFormMaritalStatusSlug(clientData.estadoCivil);
  personalData.gender = toFormGenderSlug(clientData.sexo);
  personalData.birth_city = clientData.ciudadNacimiento ?? defaults.birth_city;
  personalData.nationality =
    toFormOptionSlug(
      clientData.codigoPaisNacionalidad ?? clientData.paisNacimiento,
      'countries',
    ) || defaults.nationality;
  personalData.document_type =
    toFormOptionSlug(clientData.codigoTipoDocumento, 'documentTypes') ||
    defaults.document_type;
  personalData.document_number =
    clientData.numeroDocumento ?? defaults.document_number;
  personalData.document_expiration_date = documentPermanentValidity
    ? ''
    : isoDateToFormDate(clientData.fechaCaducidadDocumento) ||
      defaults.document_expiration_date;
  personalData.document_permanent_validity = documentPermanentValidity;
  personalData.email = clientData.email ?? defaults.email;
  personalData.phone_number = formatPhoneNumber(phoneRow) || defaults.phone_number;

  if (postalAddressRow) {
    hydratePostalAddressFromClientData(
      personalData.postal_address,
      postalAddressRow,
    );
  }

  personalData.has_different_fiscal_address = hasDifferentFiscalAddress;

  if (hasDifferentFiscalAddress && fiscalAddressRow) {
    const fiscalCountry =
      personalData.postal_address.country ||
      defaults.postal_address.country ||
      'spain';

    personalData.fiscal_address = createEmptyAddress(fiscalCountry);
    hydrateFiscalAddressFromClientData(
      personalData.fiscal_address,
      fiscalAddressRow,
      fiscalCountry,
    );
  } else {
    personalData.fiscal_address = undefined;
  }
};

const hydrateProfessionalProfileFromClientData = (
  draft: MutableAccountOpeningFormSections,
  clientData: OnboardingClientData,
): void => {
  const defaults = ACCOUNT_OPENING_DEFAULTS.professionalProfile;
  const employmentStatusSlug = toFormOptionSlug(
    clientData.tipoTrabajo,
    'employmentStatus',
  );
  const sector = toFormOptionSlug(clientData.codigoSectorActividad, 'sectors');
  const activity = toFormOptionSlug(clientData.codigoProfesion, 'activities');
  const companyName = clientData.nombreComercial?.trim() ?? '';

  if (
    employmentStatusSlug === EmploymentStatus.NotWorking ||
    clientData.tipoTrabajo === 'NOTRA'
  ) {
    draft.professionalProfile = {
      employmentStatus: EmploymentStatus.NotWorking,
    };
    return;
  }

  if (
    employmentStatusSlug === EmploymentStatus.SelfEmployed ||
    clientData.tipoTrabajo === 'PROPI'
  ) {
    draft.professionalProfile = {
      employmentStatus: EmploymentStatus.SelfEmployed,
      businessName: companyName || defaults.companyName,
      sector: sector || defaults.sector,
      mainActivity: activity || defaults.companyActivity,
    };
    return;
  }

  draft.professionalProfile = {
    employmentStatus: EmploymentStatus.Employed,
    companyName: companyName || defaults.companyName,
    sector: sector || defaults.sector,
    companyActivity: activity || defaults.companyActivity,
  };
};

const hydrateWealthSourceFundsFromKyc = (
  wealthSourceFunds: WealthSourceFundsFormData,
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
): void => {
  const defaults = ACCOUNT_OPENING_DEFAULTS.wealthSourceFunds;

  wealthSourceFunds.mainIncomeSource = getKycSingleAnswerSlug(
    kycByQuestion,
    'MAIN_INCOME',
    defaults.mainIncomeSource,
  );
  wealthSourceFunds.estimatedAnnualIncome = getKycSingleAnswerSlug(
    kycByQuestion,
    'ANNUAL_INCOME',
    defaults.estimatedAnnualIncome,
  );
  wealthSourceFunds.approximateWealth = getKycSingleAnswerSlug(
    kycByQuestion,
    'ESTIMATED_WEALTH',
    defaults.approximateWealth,
  );
  wealthSourceFunds.sourceOfFunds = getKycMultipleAnswerSlugs(
    kycByQuestion,
    'FUNDS_ORIGIN',
    defaults.sourceOfFunds,
  );
};

const hydrateAccountUsageFromKyc = (
  accountUsage: AccountUsageFormData,
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
): void => {
  const defaults = ACCOUNT_OPENING_DEFAULTS.accountUsage;

  accountUsage.accountPurpose = getKycMultipleAnswerSlugs(
    kycByQuestion,
    'ACCOUNT_PURPOSE',
    defaults.accountPurpose,
  );
  accountUsage.monthlyOperations = getKycSingleAnswerSlug(
    kycByQuestion,
    'MONTHLY_OPS',
    defaults.monthlyOperations,
  );
  accountUsage.monthlyTransfers = getKycSingleAnswerSlug(
    kycByQuestion,
    'MONTHLY_TRANSFERS',
    defaults.monthlyTransfers,
  );
  accountUsage.accountCheques = getKycSingleAnswerSlug(
    kycByQuestion,
    'ACCOUNT_CHEQUES',
    defaults.accountCheques,
  );
};

const hydrateLegalDeclarationsFromOnboarding = (
  legalDeclarations: LegalDeclarationsFormData,
  kycByQuestion: Map<number, OnboardingKycAnswerResponse>,
  clientData: OnboardingClientData | null | undefined,
): void => {
  const defaults = ACCOUNT_OPENING_DEFAULTS.legalDeclarations;

  legalDeclarations.isPoliticallyExposedPerson = hasKycYesAnswer(
    kycByQuestion,
    'PUBLIC_ROLE',
    'public-role-yes',
  );
  legalDeclarations.isRelatedToPublicPerson = hasKycYesAnswer(
    kycByQuestion,
    'PEP_RELATED',
    'pep-related-yes',
  );

  const publicRoleQuestionId = getQuestionId('PUBLIC_ROLE');
  const publicRoleCsv = getKycOpenTextCsv(
    getKycQuestionAnswers(kycByQuestion, 'PUBLIC_ROLE'),
    publicRoleQuestionId,
  );
  const [, pepPosition, pepInstitution, pepEndDate] = splitCsvParts(publicRoleCsv);

  const prpExtraIncomeQuestionId = getQuestionId('PRP_EXTRA_INCOME');
  const hasRelatedAdditionalIncome = hasKycYesAnswer(
    kycByQuestion,
    'PRP_EXTRA_INCOME',
    'prp-extra-income-yes',
  );
  const prpExtraIncomeCsv = getKycOpenTextCsv(
    getKycQuestionAnswers(kycByQuestion, 'PRP_EXTRA_INCOME'),
    prpExtraIncomeQuestionId,
  );
  const [additionalIncomeDetail] = splitCsvParts(prpExtraIncomeCsv);

  const pepRelatedQuestionId = getQuestionId('PEP_RELATED');
  const pepRelatedCsv = getKycOpenTextCsv(
    getKycQuestionAnswers(kycByQuestion, 'PEP_RELATED'),
    pepRelatedQuestionId,
  );
  const [relationship, personName, publicPosition, institution] =
    splitCsvParts(pepRelatedCsv);

  const taxResidence = clientData?.datosResidenciaFiscal?.[0];
  const taxResidenceCountrySlug = toFormOptionSlug(
    taxResidence?.codigoPais ?? clientData?.paisFiscal,
    'countries',
  );
  const isUsTaxResidence = taxResidenceCountrySlug === 'united-states';

  if (legalDeclarations.isPoliticallyExposedPerson) {
    legalDeclarations.pepDetail = {
      position: pepPosition ?? '',
      institution: pepInstitution ?? '',
      endDate: pepEndDate || undefined,
      hasRelatedAdditionalIncome,
      additionalIncomeDetail: hasRelatedAdditionalIncome
        ? additionalIncomeDetail || undefined
        : undefined,
    };
  } else {
    legalDeclarations.pepDetail = undefined;
  }

  if (legalDeclarations.isRelatedToPublicPerson) {
    legalDeclarations.publicPersonRelationshipDetail = {
      relationship: relationship ?? '',
      personName: personName ?? '',
      publicPosition: publicPosition ?? '',
      institution: institution ?? '',
    };
  } else {
    legalDeclarations.publicPersonRelationshipDetail = undefined;
  }

  legalDeclarations.isUsTaxResidence = isUsTaxResidence;
  legalDeclarations.taxResidenceCountry = isUsTaxResidence
    ? defaults.taxResidenceCountry
    : taxResidenceCountrySlug || defaults.taxResidenceCountry;
  legalDeclarations.taxResidenceDocumentNumber =
    taxResidence?.numeroDocumentoPais?.trim() ||
    defaults.taxResidenceDocumentNumber;
};
